import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {UntypedFormBuilder, UntypedFormGroup} from '@angular/forms';
import {Observable} from 'rxjs';
import {first} from 'rxjs/operators';
import {ButtonsTranslations} from '~shared/components/buttons/buttons-translations.service';
import {SpinnerService} from '~shared/components/buttons/spinner/spinner.service';
import {FormLabelTranslationService} from '~shared/components/form-fields/translations/form-label-translations.service';
import {TixxSelectTranslationsService} from '~shared/translations';
import {NotificationType} from '~core/enum';
import {DefaultNotificationService, TixxSelectService} from '~core/services';
import {Lottery} from '~core/models';
import {EventAdministrationService} from "~features/tixx-select/event-administration.service";

/**
 * Component that lets you interact with the webpage individualization settings
 */
@Component({
  selector: 'app-webpage-individualization',
  templateUrl: './webpage-individualization.component.html'
})
export class WebpageIndividualizationComponent implements OnInit, OnDestroy {
  public fileToUpload: File = null;
  public webpageIndividualizationForm: UntypedFormGroup;
  public spinnerName: string = 'heroImageSubmit';

  public isValidFileType: boolean = false;
  public allowedTypes: Array<string> = [
    'image/gif',
    'image/jpeg',
    'image/jpg',
    'image/png'
  ];

  @Input() public selectedLottery: Lottery;

  constructor(
    public tixxSelectService: TixxSelectService,
    public buttonsTranslations: ButtonsTranslations,
    public tixxSelectOverviewService: EventAdministrationService,
    private tixxSelectTranslationService: TixxSelectTranslationsService,
    private notificationService: DefaultNotificationService,
    private formBuilder: UntypedFormBuilder,
    public flTranslations: FormLabelTranslationService,
    public spinnerService: SpinnerService,
  ) {
  }

  ngOnInit(): void {
    const event = this.selectedLottery.event;

    this.webpageIndividualizationForm = this.formBuilder.group({
      id: [this.selectedLottery.id],
      event: this.formBuilder.group({
        specialRequirementText: [event.specialRequirementText],
        specialRequirementDescription: [event.specialRequirementDescription],
        tixxId: [event.tixxId],
        name: [event.name],
        competition: [event.competition],
        date: [event.date.date],
        venue: [event.venue],
        heroImage: [event.heroImage],
        homeTeam: [event.homeTeam],
        homeTeamLogoUrl: [event.homeTeamLogoUrl],
        guestTeam: [event.guestTeam],
        guestTeamLogoUrl: [event.guestTeamLogoUrl],
        guidelineHeadline: [event.guidelineHeadline],
        guidelineText: [event.guidelineText]
      })
    });
  }

  ngOnDestroy(): void {
    this.tixxSelectOverviewService.toggleEditWebpageIndividualizationModal(false);
  }

  /**
   * Upload a logo for a club-overview.
   */
  public uploadLogo(): Observable<any> {
    const formData: FormData = new FormData();
    formData.append('file', this.fileToUpload);
    return this.tixxSelectService.postHeroImage(formData);
  }

  /**
   * Submits the form to the lottery endpoint
   */
  public async submitForm(): Promise<void> {
    let logoResponse;
    if (this.fileToUpload && this.isValidFileType) {
      logoResponse = await this.uploadLogo().toPromise();
      this.webpageIndividualizationForm.get('event').get('heroImage').patchValue(logoResponse['@id']);
    }

    this.tixxSelectService.updateLottery(this.selectedLottery.id, this.webpageIndividualizationForm.value).pipe(first())
      .subscribe({
        next: (res: Lottery) => {
          this.tixxSelectService.setSelectedLottery(res);
          this.spinnerService.hide(this.spinnerName);
          this.tixxSelectOverviewService.toggleEditWebpageIndividualizationModal(false);
          this.notificationService.showNotification({
            type: NotificationType.SUCCESS,
            message: this.tixxSelectTranslationService.i18nSuccessUpdateLottery
          });
        },
        error: (error: Error) => {
          this.notificationService.showNotification({
            type: NotificationType.ALERT,
            message: this.tixxSelectTranslationService.i18nErrorUpdateLottery
          });
          this.spinnerService.hide(this.spinnerName);
        }
      });
  }

  /**
   * Verifies that a file is of type .gif, .jpg, .png.
   */
  public fileChange(eventTarget: any): void {
    if (eventTarget.files && eventTarget.files.length > 0) {
      const file = eventTarget.files[0];

      if (this.isAllowedType(file)) {
        this.fileToUpload = file;
        this.isValidFileType = true;
        return;
      }

      eventTarget.value = '';
      this.notificationService.showNotification({
        type: NotificationType.ALERT,
        message: 'Wrong type'
      });
      this.fileToUpload = null;
      this.isValidFileType = false;
    }
  }

  /**
   * Is provided file allowed.
   */
  private isAllowedType(file: File): boolean {
    return this.allowedTypes.some((type: string) => file.type === type);
  }

}
